import {
  Anchor,
  Button,
  Card,
  Center,
  Divider,
  Stack,
  ThemeIcon,
  Title,
  Image,
  TypographyStylesProvider,
  Code,
  Box,
} from "@mantine/core";
import React from "react";
import DocsHighlightCard from "../articlesHelpers/DocsHighlightCard";
import DocsButton from "../articlesHelpers/DocsButton";
import {
  IconArrowBigDownFilled,
  IconArrowBigUpFilled,
  IconCircleMinus,
  IconExchange,
  IconExternalLink,
} from "@tabler/icons-react";
import { JsonView, allExpanded, darkStyles } from "react-json-view-lite";
import SimpleImage from "../articlesHelpers/SimpleImage";

const channelData = {
  alias: "Rizful Node LSP B",
  capacity: 500000,
  channel_opening_transaction_data: {
    blockhash:
      "00000000000000000000e6a242ed6e65eac782f3f537a75805a9f07ad2b1a6ad",
    blocktime: 1731459376,
    confirmations: 91,
    hash: "555addb8485f303d99345ad1648ee2ce395bd3a7bb6bcbcebc674caacb3b84ec",
    hex: "0200000000010180878ca73fae389cc94e5a8e527fb927c39e37a360197f31f19e077ae0582fed0100000000000000000249f7020000000000225120a105cf752676df02859d582ad570fb29b19747812386eac9585bb238ebb112b420a1070000000000220020869823813d1e02fc4c7692d9b00d5129314d015ac7737cb851b82dcb44f0b64701408db6509a71de2579a255064e06adcd0d56a50f062f7892a7fb515875a267a0985235ce1a80cf6fe5d03a4ff2d11c6d1fa9e309c71797c6e82a20547f6408841200000000",
    locktime: 0,
    size: 205,
    time: 1731459376,
    txid: "e7f3a5e7fa9923edc6ee68fd12e9143163cf8df5ad7337102ca07375fd64469a",
    version: 2,
    vsize: 154,
    weight: 616,
    vin: [],
    vout: [],
  },
  channel_point:
    "e7f3a5e7fa9923edc6ee68fd12e9143163cf8df5ad7337102ca07375fd64469a:1",
  confirmation_count: 91,
  is_active: true,
  is_closing: false,
  is_opening: false,
  is_partner_initiated: true,
  is_private: true,
  local_balance: 100000,
  local_csv: 144,
  local_reserve: 354,
  partner_public_key:
    "02281dfeb76cf67a252e50e5765f2c6ecddf2aa726f99be8eae8d1316ba2990f79",
  pending_payments: [],
  received: 100000,
  remote_balance: 396530,
  remote_reserve: 5000,
  scid: "870066x2561x1",
  sent: 0,
  synthetic_status: "open",
  time_offline: 65000,
  time_online: 264045000,
  unsettled_balance: 0,
};

export const CHANNEL_INFO_CONTENT = (
  <TypographyStylesProvider>
    <p>The Channel Info button looks like an abacus:</p>

    <SimpleImage src="docs_images/channel-info-button.png" h={250} />
    <p>
      Here we see critical information about the channel, and a button to see
      the opening transaction on a block explorer, which looks like this:
    </p>

    <DocsButton
      ariaLabel="View transaction"
      text="Transaction"
      leftSection={<IconExternalLink stroke={1.5} />}
    />
    <p>We also have a lot of details about the channel:</p>
    <Box mb="20px">
      <JsonView
        data={channelData}
        shouldExpandNode={allExpanded}
        style={darkStyles}
      />
    </Box>
    <Divider my="20px" />
    <Title order={3}>Let's dig into this...</Title>
    <ul>
      <li>
        <strong>alias:</strong> The human-readable name for the channel. Not all
        peers will have an alias, but many will.
      </li>
      <li>
        <strong>capacity:</strong> The total capacity of the channel in satoshis
        (500,000 satoshis here).
      </li>

      <li>
        <strong>channel_point:</strong> Unique identifier for the channel,
        combining transaction ID and output index.
      </li>
      <li>
        <strong>confirmation_count:</strong> Number of confirmations for the
        channel's funding transaction. Due a quirk with Rizful, sometimes this
        number won't go about 12, even when there have been more than 12
        confirmations.
      </li>

      <li>
        <strong>local_csv:</strong> Local CSV delay (in blocks). The CSV delay
        is the number of blocks that must pass before the local party can spend
        funds in the channel, in the case that the channel closes unilaterally.
      </li>
      <li>
        <strong>local_reserve:</strong> Minimum reserve amount for the local
        side. The reserve is an amount that that node must keep in the channel
        to ensure that it can pay fees to close the channel.
      </li>
      <li>
        <strong>partner_public_key:</strong> The public key of the remote node.
      </li>
      <li>
        <strong>pending_payments:</strong> List of payments still in flight
        (none here).
      </li>
      <li>
        <strong>received:</strong> Total amount received through the channel.
      </li>
      <li>
        <strong>remote_balance:</strong> The balance available on the remote
        side.
      </li>
      <li>
        <strong>remote_reserve:</strong> Minimum reserve amount for the remote
        side. Again, this is the amount that the remote node must keep in the
        channel to ensure that it can pay fees to close the channel.
      </li>
      <li>
        <strong>scid:</strong> Short Channel ID. You'll also see this referred
        to as the SCID. It's a unique identifier for the channel. It's
        determined by the block height of the funding transaction, the
        transaction index within the block, and the output index of the funding
        transaction. Each of those number is an integer, and then the numbers
        are all connected with "x" characters. So, for example, 870066x2561x1.
        You don't really need to memorize that, but it's fun to know.
      </li>
    </ul>
  </TypographyStylesProvider>
);
